import { DestroyRef, inject, Injectable } from '@angular/core';
import { filter, fromEvent, map, NEVER, Observable, share } from 'rxjs';
import { OutgoingWindowChannelMessage, WINDOW_CHANNEL_NAME, WindowChannelMessage, WindowChannelMessageType } from '../../models/window-channel.model';

const WINDOW_ID_KEY = 'lh_window_id';
const PEER_KEY_PREFIX = 'lh_window_peer_';

// Same-origin messaging between this app's windows and tabs over BroadcastChannel.
// Messages are broadcast to every window, so each carries its sender id and an optional
// recipient id, and a window only receives broadcasts and messages addressed to it.
@Injectable({
    providedIn: 'root'
})
export class WindowChannelService {
    private readonly channel: BroadcastChannel | null = typeof BroadcastChannel === 'function'
        ? new BroadcastChannel(WINDOW_CHANNEL_NAME)
        : null;

    private readonly incoming$: Observable<WindowChannelMessage> = this.channel == null
        ? NEVER
        : fromEvent<MessageEvent<WindowChannelMessage>>(this.channel, 'message').pipe(
            map((event) => event.data),
            share(),
        );

    readonly isSupported: boolean = this.channel != null;

    private _windowId: string = crypto.randomUUID();

    constructor() {
        inject(DestroyRef).onDestroy(() => this.channel?.close());
    }

    get windowId(): string {
        return this._windowId;
    }

    post(message: OutgoingWindowChannelMessage): void {
        this.channel?.postMessage({ ...message, from: this._windowId });
    }

    messages<T extends WindowChannelMessageType>(type: T): Observable<Extract<WindowChannelMessage, { type: T }>> {
        return this.incoming$.pipe(
            filter((message): message is Extract<WindowChannelMessage, { type: T }> => {
                return message.type === type && (message.to == null || message.to === this._windowId);
            }),
        );
    }

    // Keeps this window's id across reloads, so peers that addressed it before the reload
    // still reach it. Opt-in only: a duplicated tab copies sessionStorage, which would give
    // two tabs the same id.
    persistWindowId(): void {
        const storedId = this.readSession(WINDOW_ID_KEY);
        if (storedId != null) {
            this._windowId = storedId;
            return;
        }

        this.writeSession(WINDOW_ID_KEY, this._windowId);
    }

    rememberPeer(role: string, windowId: string): void {
        this.writeSession(`${PEER_KEY_PREFIX}${role}`, windowId);
    }

    peer(role: string): string | null {
        return this.readSession(`${PEER_KEY_PREFIX}${role}`);
    }

    private readSession(key: string): string | null {
        try {
            return sessionStorage.getItem(key);
        } catch {
            return null;
        }
    }

    private writeSession(key: string, value: string): void {
        try {
            sessionStorage.setItem(key, value);
        } catch {
            // Storage blocked: the window keeps a per-load id and forgets its peers on reload.
        }
    }
}
